import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { AppConfig } from '@/constants/residents/config';
import { LegalContentType } from '@/screens/residents/legal/LegalScreen';

interface LegalModalProps {
  visible: boolean;
  onClose: () => void;
  contentType: LegalContentType;
}

export const LegalModal: React.FC<LegalModalProps> = ({ visible, onClose, contentType }) => {
  const { semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getTitle = (): string => {
    switch (contentType) {
      case 'privacy':
        return 'Privacy Policy';
      case 'terms':
        return 'Terms & Conditions';
      default:
        return 'Legal';
    }
  };

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const url = contentType === 'terms' ? AppConfig.URLS.TERMS : AppConfig.URLS.PRIVACY;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch content: ${response.status}`);
      }
      const htmlContent = await response.text();
      setContent(htmlContent);
    } catch (err) {
      console.error('Error fetching legal content:', err);
      setError('Failed to load content. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [contentType]);

  useEffect(() => {
    if (visible && Platform.OS === 'web') {
      fetchContent();
    }
  }, [visible, fetchContent]);

  // Only render on web platform
  if (Platform.OS !== 'web') {
    return null;
  }

  // With page-based navigation on web, this component should rarely render.
  if (!visible) return null;

  return (
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>{getTitle()}</Text>
            <Pressable style={styles.closeButton} onPress={onClose}>
              <Text style={styles.closeButtonText}>✕</Text>
            </Pressable>
          </View>

          {/* Content */}
          <View style={styles.contentContainer}>
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={semantic.text.primary} />
                <Text style={styles.loadingText}>Loading...</Text>
              </View>
            ) : error ? (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>{error}</Text>
                <Pressable style={styles.retryButton} onPress={fetchContent}>
                  <Text style={styles.retryButtonText}>Retry</Text>
                </Pressable>
              </View>
            ) : (
              <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={true}>
                <View style={styles.htmlContainer}>
                  {/* For web, we can use dangerouslySetInnerHTML equivalent */}
                  <div
                    style={{
                      color: semantic.text.secondary,
                      backgroundColor: semantic.background.primary,
                      fontSize: 14,
                      lineHeight: 1.6,
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </View>
              </ScrollView>
            )}
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Pressable style={styles.closeFooterButton} onPress={onClose}>
              <Text style={styles.closeFooterButtonText}>Close</Text>
            </Pressable>
          </View>
        </View>
      </View>
  );
};

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    ...Platform.select({
      web: {
        position: 'fixed' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
      },
    }),
  },
  modalContainer: {
    backgroundColor: semantic.background.primary,
    borderRadius: 12,
    maxWidth: 600,
    width: '100%',
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: semantic.border.secondary,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: semantic.text.primary,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.2)' as any,
        },
      },
    }),
  },
  closeButtonText: {
    fontSize: 16,
    color: semantic.text.primary,
    fontWeight: 'bold',
  },
  contentContainer: {
    flex: 1,
    minHeight: 300,
    backgroundColor: semantic.background.primary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    backgroundColor: semantic.background.primary,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: semantic.text.secondary,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorText: {
    fontSize: 16,
    color: semantic.status.error,
    textAlign: 'center',
    marginBottom: 20,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  retryButton: {
    backgroundColor: semantic.interactive.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        '&:hover': {
          backgroundColor: '#2563EB' as any,
        },
      },
    }),
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  scrollView: {
    flex: 1,
  },
  htmlContainer: {
    padding: 24,
  },
  footer: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: semantic.border.secondary,
    alignItems: 'flex-end',
  },
  closeFooterButton: {
    backgroundColor: semantic.interactive.secondary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 6,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        '&:hover': {
          backgroundColor: '#2563EB' as any,
        },
      },
    }),
  },
  closeFooterButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
});
